import type { ExperienceIndexCatalogCompetency } from "../queries";
import type { CareerPlanAssessmentAnswer } from "./careerPlanHistory";

export type ExperienceIndexAssessmentOptionView = {
  optionId: string;
  optionContent: string;
  selected: boolean;
};

export type ExperienceIndexAssessmentQuestionView = {
  questionId: string;
  questionContent: string;
  options: ExperienceIndexAssessmentOptionView[];
};

export type ExperienceIndexAssessmentCompetencyView = {
  competencyId: string;
  competencyName: string;
  slug: string | null;
  questions: ExperienceIndexAssessmentQuestionView[];
};

export function groupExperienceIndexAnswers(answers: CareerPlanAssessmentAnswer[]) {
  const groups: CareerPlanAssessmentAnswer[][] = [];
  const indexByCompetencyId = new Map<string, number>();

  for (const answer of answers) {
    const existingIndex = indexByCompetencyId.get(answer.competencyId);
    if (existingIndex == null) {
      indexByCompetencyId.set(answer.competencyId, groups.length);
      groups.push([answer]);
      continue;
    }

    groups[existingIndex].push(answer);
  }

  return groups;
}

export function focusedExperienceIndexAnswerGroups(
  answers: CareerPlanAssessmentAnswer[],
  focusedCompetencyId?: string | null
) {
  const groups = groupExperienceIndexAnswers(answers);
  if (!focusedCompetencyId) {
    return groups;
  }

  return groups.filter((group) => group[0].competencyId === focusedCompetencyId);
}

function selectedOptionView(answer: CareerPlanAssessmentAnswer): ExperienceIndexAssessmentOptionView {
  return {
    optionId: answer.optionId,
    optionContent: answer.optionContent,
    selected: true
  };
}

function selectedOnlyQuestionViews(answers: CareerPlanAssessmentAnswer[]): ExperienceIndexAssessmentQuestionView[] {
  return answers.map((answer) => ({
    questionId: answer.questionId,
    questionContent: answer.questionContent,
    options: [selectedOptionView(answer)]
  }));
}

function catalogQuestionViews(
  catalog: ExperienceIndexCatalogCompetency,
  answers: CareerPlanAssessmentAnswer[]
): ExperienceIndexAssessmentQuestionView[] {
  const savedByQuestionId = new Map(answers.map((answer) => [answer.questionId, answer]));
  const catalogQuestionIds = new Set(catalog.questions.map((question) => question.questionId));

  const questions = catalog.questions.map((question) => {
    const saved = savedByQuestionId.get(question.questionId);
    const options: ExperienceIndexAssessmentOptionView[] = [...question.options]
      .sort((left, right) => right.score - left.score)
      .map((option) => ({
        optionId: option.optionId,
        optionContent: option.optionContent,
        selected: saved?.optionId === option.optionId
      }));

    if (saved && !options.some((option) => option.selected)) {
      options.push(selectedOptionView(saved));
    }

    return {
      questionId: question.questionId,
      questionContent: question.questionContent,
      options
    };
  });

  const orphanedQuestions = selectedOnlyQuestionViews(
    answers.filter((answer) => !catalogQuestionIds.has(answer.questionId))
  );

  return [...questions, ...orphanedQuestions];
}

export function selectedOptionIdsByQuestion(groups: ExperienceIndexAssessmentCompetencyView[]) {
  const next: Record<string, string> = {};

  for (const group of groups) {
    for (const question of group.questions) {
      const selected = question.options.find((option) => option.selected);
      if (selected) {
        next[question.questionId] = selected.optionId;
      }
    }
  }

  return next;
}

export function experienceIndexSelectionsEqual(left: Record<string, string>, right: Record<string, string>) {
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);

  for (const key of keys) {
    if (left[key] !== right[key]) {
      return false;
    }
  }

  return true;
}

export function liveCatalogCompetency(competencies: ExperienceIndexCatalogCompetency[], competencyId?: string | null) {
  if (!competencyId) return undefined;
  return competencies.find((competency) => competency.competencyId === competencyId);
}

export function hasLiveCatalogQuestions(catalog: ExperienceIndexCatalogCompetency | undefined) {
  return Boolean(catalog && catalog.questions.length > 0);
}

function isLiveCatalogOption(catalog: ExperienceIndexCatalogCompetency, questionId: string, optionId: string) {
  const question = catalog.questions.find((item) => item.questionId === questionId);
  return Boolean(question?.options.some((option) => option.optionId === optionId));
}

export function liveCatalogSelectionBlocked(
  catalog: ExperienceIndexCatalogCompetency | undefined,
  selections: Record<string, string>
) {
  if (!hasLiveCatalogQuestions(catalog) || !catalog) return true;

  return catalog.questions.some((question) => {
    const optionId = selections[question.questionId];
    if (!optionId) return false;
    return !isLiveCatalogOption(catalog, question.questionId, optionId);
  });
}

export function competencyAnswersPayload(
  competencyId: string,
  catalog: ExperienceIndexCatalogCompetency | undefined,
  selections: Record<string, string>
) {
  if (!catalog || catalog.competencyId !== competencyId) {
    return [];
  }

  return catalog.questions.flatMap((question) => {
    const optionId = selections[question.questionId];
    if (!optionId || !isLiveCatalogOption(catalog, question.questionId, optionId)) {
      return [];
    }

    return [{ questionId: question.questionId, optionId }];
  });
}

export function getExperienceIndexAssessmentView(
  competencies: ExperienceIndexCatalogCompetency[],
  answers: CareerPlanAssessmentAnswer[],
  focusedCompetencyId?: string | null
): ExperienceIndexAssessmentCompetencyView[] {
  const catalogById = new Map(competencies.map((competency) => [competency.competencyId, competency]));
  const competencyIds = focusedCompetencyId
    ? [focusedCompetencyId]
    : [...new Set(answers.map((answer) => answer.competencyId))];

  return competencyIds.flatMap((competencyId) => {
    const answersForCompetency = answers.filter((answer) => answer.competencyId === competencyId);
    const catalog = catalogById.get(competencyId);

    if (answersForCompetency.length === 0) {
      if (!focusedCompetencyId || !catalog || catalog.questions.length === 0) {
        return [];
      }

      return [
        {
          competencyId: catalog.competencyId,
          competencyName: catalog.competencyName,
          slug: catalog.slug,
          questions: catalogQuestionViews(catalog, [])
        }
      ];
    }

    if (!catalog || catalog.questions.length === 0) {
      return [
        {
          competencyId: answersForCompetency[0].competencyId,
          competencyName: answersForCompetency[0].competencyName,
          slug: catalog?.slug ?? null,
          questions: selectedOnlyQuestionViews(answersForCompetency)
        }
      ];
    }

    return [
      {
        competencyId: catalog.competencyId,
        competencyName: catalog.competencyName,
        slug: catalog.slug,
        questions: catalogQuestionViews(catalog, answersForCompetency)
      }
    ];
  });
}
